import { FC, useState } from 'react';
import { Category, ShopContent } from '../../../types/cms_shared_types';
import { Conditional } from '../../Common/Conditional';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { TooltipHelper } from '../TooltipHelper';
import { SubcategoryItem } from './SubcategoryItem';
import { SubcategoryPickerMobile } from './SubcategoryPickerMobile';
import { Product, ProductListActions } from '../../../types/webshop';

interface SubcategoryPickerProps {
  category: Category;
  selectedProducts: Product[];
  selectedProductsActions: ProductListActions;
  selectedSubcategoryKey: string | undefined;
  setSelectedSubcategoryKey: (value: string) => void;
  cmsShopContent: ShopContent;
}

export const SubcategoryPicker: FC<SubcategoryPickerProps> = ({
  category,
  selectedProducts,
  selectedProductsActions,
  selectedSubcategoryKey,
  setSelectedSubcategoryKey,
  cmsShopContent,
}) => {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [pendingNewSubcategoryKey, setPendingNewSubcategoryKey] = useState('');

  const switchSubcategories = (newSelectedSubcategoryKey: string) => {
    if (selectedProducts.length > 0 && selectedSubcategoryKey !== newSelectedSubcategoryKey) {
      setPendingNewSubcategoryKey(newSelectedSubcategoryKey);
      setShowConfirmationDialog(true);
      return;
    }

    setSelectedSubcategoryKey(newSelectedSubcategoryKey);
  };

  return (
    <div className="flex flex-row justify-between mb-4 sm:flex-col sm:mb-0 sm:mx-0">
      <div className="flex my-auto sm:flex-row sm:items-center">
        <div className="mr-2 text-base font-extrabold text-text-dark">
          {cmsShopContent.productChoiceStep.categoryHeader}
        </div>
        <TooltipHelper text={cmsShopContent.productChoiceStep.subcategoryTooltip} />
      </div>

      <div className="sm:hidden">
        <SubcategoryPickerMobile
          subcategories={category?.subcategories}
          selectedSubcategoryKey={selectedSubcategoryKey}
          cmsShopContent={cmsShopContent}
          switchSubcategories={switchSubcategories}
        />
      </div>

      <div className="flex-col hidden sm:flex">
        {category?.subcategories?.map((subcategory, index) => {
          return (
            <SubcategoryItem
              key={index}
              name={subcategory.name}
              isSelected={selectedSubcategoryKey === subcategory.key}
              onClick={() => switchSubcategories(subcategory.key)}
            />
          );
        })}
      </div>

      <Conditional
        condition={showConfirmationDialog}
        trueRender={
          <ConfirmationDialog
            showModal={true}
            setShowModal={setShowConfirmationDialog}
            onYesCallback={() => {
              selectedProductsActions.reset();
              setSelectedSubcategoryKey(pendingNewSubcategoryKey);
            }}
            title={cmsShopContent.confirmationDialog.titleWhenSwitchingSubcategory}
            description={cmsShopContent.confirmationDialog.descriptionWhenSwitchingSubcategory}
            cmsContent={cmsShopContent}
          />
        }
      />
    </div>
  );
};
